import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, PlayCircle, StopCircle, CheckCircle2, AlertTriangle, MapPin, Car, Satellite, Fuel } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { fmtDateTime } from "../lib/format";

function TelematicsCheck({ check }) {
  if (!check) return null;
  return (
    <div className={`border rounded-lg p-3 ${check.synced ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="ta-telematics-check">
      <p className={`text-sm font-semibold flex items-center gap-2 ${check.synced ? "text-emerald-700" : "text-amber-800"}`}>
        <Satellite className="h-4 w-4" />
        {check.synced ? "Selari dengan Data Telematik" : "Tidak Selari dengan Data Telematik"}
      </p>
      <div className="text-xs mt-2 space-y-1 text-slate-700">
        <p>Jarak GPS (telematik): <b>{check.gps_distance_km} km</b></p>
        <p>Jarak direkod (odometer): <b>{check.user_distance_km} km</b></p>
        <p>Varians: <b>{check.variance_pct}%</b> {check.synced ? "(dalam had 15%)" : "(melebihi had 15% — akan disemak)"}</p>
        {check.start_variance_min != null && (
          <p>Masa mula: {Math.abs(check.start_variance_min)} minit {check.start_variance_min >= 0 ? "selepas" : "sebelum"} jadual</p>
        )}
      </div>
    </div>
  );
}

export default function TripAction() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [startForm, setStartForm] = useState({ start_datetime: "", odometer_start: "", notes: "" });
  const [endForm, setEndForm] = useState({ end_datetime: "", odometer_end: "", notes: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/bookings/${bookingId}`);
    setDetail(data);
    setStartForm((f) => ({
      ...f,
      start_datetime: f.start_datetime || data.booking.start_datetime?.slice(0, 16) || "",
      odometer_start: f.odometer_start || (data.vehicle?.current_odometer ?? ""),
    }));
    setEndForm((f) => ({
      ...f,
      end_datetime: f.end_datetime || data.booking.end_datetime?.slice(0, 16) || "",
    }));
  }, [bookingId]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  if (!detail) return <p className="text-sm text-muted-foreground">Memuatkan...</p>;

  const b = detail.booking;
  const trip = detail.trip;
  const running = trip?.trip_status === "berjalan" || b.trip_status === "berjalan";
  const done = b.trip_logged || trip?.trip_status === "selesai" || (trip && trip.trip_status === undefined);
  const canAct = b.status === "diluluskan" && !done;

  const doStart = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/bookings/${b.id}/trip/start`, {
        start_datetime: startForm.start_datetime,
        odometer_start: Number(startForm.odometer_start),
        notes: startForm.notes || null,
      });
      toast.success("Perjalanan dimulakan. Selamat memandu!");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doEnd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${b.id}/trip/end`, {
        end_datetime: endForm.end_datetime,
        odometer_end: Number(endForm.odometer_end),
        fuel_liters: 0,
        fuel_cost_rm: 0,
        notes: endForm.notes || null,
      });
      setResult(data);
      toast.success("Perjalanan tamat & direkodkan.");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const finalTrip = result || trip;

  return (
    <div className="space-y-4 fade-up max-w-lg mx-auto" data-testid="trip-action-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#10056E]" data-testid="ta-back-btn">
        <ArrowLeft className="h-4 w-4" /> Kembali
      </button>

      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {done ? "Perjalanan Selesai" : running ? "Tamat Perjalanan" : "Mula Perjalanan"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Laraskan maklumat jika perlu sebelum mengesahkan.</p>
      </div>

      {/* Booking summary */}
      <Card className="border-slate-200 shadow-sm rounded-xl">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-[#10056E] shrink-0" />
            <span className="font-semibold">{b.destination}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Car className="h-4 w-4 text-[#10056E] shrink-0" />
            <span>{b.vehicle_reg || "Belum ditugaskan"} {detail.vehicle ? `— ${detail.vehicle.make} ${detail.vehicle.model}` : ""}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Jadual: {fmtDateTime(b.start_datetime)} → {fmtDateTime(b.end_datetime)} · Anggaran {b.estimated_distance} km
          </p>
        </CardContent>
      </Card>

      {b.status !== "diluluskan" && !done && (
        <Card className="border-amber-300 bg-amber-50 rounded-xl">
          <CardContent className="p-4 text-sm text-amber-800" data-testid="ta-not-approved">
            Tempahan ini berstatus <b>{b.status}</b>. Perjalanan hanya boleh dimulakan selepas tempahan diluluskan.
          </CardContent>
        </Card>
      )}

      {/* START FORM */}
      {canAct && !running && (
        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <PlayCircle className="h-5 w-5 text-[#198754]" /> Sahkan Mula Perjalanan
            </CardTitle>
            <CardDescription className="text-xs">Rekod ini akan disemak silang dengan data telematik kenderaan.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={doStart} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Tarikh & Masa Mula Sebenar</Label>
                <Input type="datetime-local" required className="h-11" value={startForm.start_datetime}
                  onChange={(e) => setStartForm({ ...startForm, start_datetime: e.target.value })} data-testid="ta-start-datetime" />
              </div>
              <div className="space-y-1.5">
                <Label>Odometer Mula (KM)</Label>
                <Input type="number" min="0" step="0.1" required className="h-11" value={startForm.odometer_start}
                  onChange={(e) => setStartForm({ ...startForm, odometer_start: e.target.value })} data-testid="ta-odo-start" />
                {detail.vehicle && <p className="text-xs text-muted-foreground">Bacaan semasa kenderaan: {detail.vehicle.current_odometer} km</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Catatan (pilihan)</Label>
                <Textarea value={startForm.notes} onChange={(e) => setStartForm({ ...startForm, notes: e.target.value })} data-testid="ta-start-notes" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-12 text-base bg-[#198754] hover:bg-[#157347]" data-testid="ta-start-confirm-btn">
                <PlayCircle className="h-5 w-5 mr-2" /> Sahkan & Mula Perjalanan
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* END FORM */}
      {canAct && running && (
        <Card className="border-slate-200 shadow-sm rounded-xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <StopCircle className="h-5 w-5 text-[#DC3545]" /> Sahkan Tamat Perjalanan
            </CardTitle>
            <CardDescription className="text-xs">
              Dimulakan {fmtDateTime(trip?.start_datetime)} · Odometer mula {trip?.odometer_start} km
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={doEnd} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Tarikh & Masa Tamat Sebenar</Label>
                <Input type="datetime-local" required className="h-11" value={endForm.end_datetime}
                  onChange={(e) => setEndForm({ ...endForm, end_datetime: e.target.value })} data-testid="ta-end-datetime" />
              </div>
              <div className="space-y-1.5">
                <Label>Odometer Akhir (KM)</Label>
                <Input type="number" min="0" step="0.1" required className="h-11" value={endForm.odometer_end}
                  onChange={(e) => setEndForm({ ...endForm, odometer_end: e.target.value })} data-testid="ta-odo-end" />
              </div>
              <div className="flex items-start gap-2.5 bg-indigo-50 border border-indigo-200 rounded-lg p-3" data-testid="ta-fuel-info">
                <Fuel className="h-4 w-4 text-[#10056E] shrink-0 mt-0.5" />
                <p className="text-xs text-[#10056E]">
                  Isi minyak semasa perjalanan ini? Rekodkan melalui menu <b>Isi Minyak</b> berserta resit —
                  kos akan dipadankan secara automatik dengan tempahan ini.
                </p>
              </div>
              {endForm.odometer_end && trip?.odometer_start != null && Number(endForm.odometer_end) > trip.odometer_start && (
                <div className="bg-slate-50 border rounded p-3 text-sm" data-testid="ta-auto-calc">
                  Jarak: <b>{(Number(endForm.odometer_end) - trip.odometer_start).toFixed(1)} km</b>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Catatan (pilihan)</Label>
                <Textarea value={endForm.notes} onChange={(e) => setEndForm({ ...endForm, notes: e.target.value })} data-testid="ta-end-notes" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-12 text-base bg-[#DC3545] hover:bg-[#bb2d3b]" data-testid="ta-end-confirm-btn">
                <StopCircle className="h-5 w-5 mr-2" /> Sahkan & Tamat Perjalanan
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* COMPLETED SUMMARY */}
      {done && finalTrip && (
        <Card className="border-slate-200 shadow-sm rounded-xl" data-testid="ta-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-[#198754]" /> Ringkasan Perjalanan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Jarak</p><p className="font-bold">{finalTrip.distance_km} km</p></div>
              <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Kadar Km/L</p><p className="font-bold">{finalTrip.kmpl ?? "-"} Km/L</p></div>
              <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Odometer</p><p className="font-bold">{finalTrip.odometer_start} → {finalTrip.odometer_end}</p></div>
              <div className="bg-slate-50 border rounded p-3"><p className="text-xs text-muted-foreground">Bahan Api</p><p className="font-bold">{finalTrip.fuel_liters} L / RM {Number(finalTrip.fuel_cost_rm || 0).toFixed(2)}</p></div>
            </div>
            <TelematicsCheck check={finalTrip.telematics_check} />
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Link to={`/tempahan/${b.id}`} className="flex-1">
                <Button variant="outline" className="w-full h-11" data-testid="ta-view-booking-btn">Lihat Tempahan</Button>
              </Link>
              <Link to="/" className="flex-1">
                <Button className="w-full h-11 bg-[#10056E] hover:bg-[#2a17a8]" data-testid="ta-dashboard-btn">Ke Dashboard</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
